import {
    computed,
    signal,
    type Signal,
    type WritableSignal,
} from '@angular/core';

export interface ActiveFilterSummary {
    readonly clearLabel: string;
    readonly key: string;
    readonly label: string;
    readonly selectionKey?: string;
    readonly value: string;
}

export interface FilterSelection<Action> {
    readonly key: string;
    readonly value: string;
    readonly clearAction: Action;
}

export interface FilterDimension<State, Action> {
    readonly key: string;
    readonly label: string;
    clearAction(state: State): Action;
    describe(state: State): string | null;
    selections?(state: State): readonly FilterSelection<Action>[];
    isActive(state: State): boolean;
}

export interface FilterDefinition<State, Action> {
    readonly dimensions: readonly FilterDimension<State, Action>[];
    createDefault(): State;
    equals(left: State, right: State): boolean;
    reduce(state: State, action: Action): State;
}

/**
 * Non-generic view of a filter controller used by shared presentation.
 * Feature controls keep their typed `FilterController` reference for draft state
 * and action dispatch.
 */
export interface FilterWorkspaceController {
    readonly activeCount: Signal<number>;
    readonly appliedActiveKeys: Signal<readonly string[]>;
    readonly appliedSummaries: Signal<readonly ActiveFilterSummary[]>;
    readonly isDefault: Signal<boolean>;
    readonly isDirty: Signal<boolean>;
    readonly summaries: Signal<readonly ActiveFilterSummary[]>;
    apply(): void;
    begin(): void;
    cancel(): void;
    clearDimension(key: string): void;
    clearSummary(key: string, selectionKey?: string): void;
    resetDraft(): void;
}

export interface FilterController<
    State,
    Action,
> extends FilterWorkspaceController {
    readonly applied: Signal<State>;
    readonly draft: Signal<State>;
    applyNow(action: Action): void;
    dispatch(action: Action): void;
    resetApplied(): void;
}

export interface CreateFilterControllerOptions<State, Action> {
    readonly applied: Signal<State>;
    readonly definition: FilterDefinition<State, Action>;
    readonly commit: (next: State) => void;
}

export function createFilterController<State, Action>(
    options: CreateFilterControllerOptions<State, Action>
): FilterController<State, Action> {
    const draft: WritableSignal<State> = signal(options.applied());

    const appliedActiveDimensions = computed(() =>
        options.definition.dimensions.filter((dimension) =>
            dimension.isActive(options.applied())
        )
    );

    const describeActiveDimensions = (
        state: State
    ): readonly ActiveFilterSummary[] =>
        options.definition.dimensions.flatMap((dimension) => {
            if (!dimension.isActive(state)) return [];

            if (dimension.selections) {
                return dimension.selections(state).map((selection) => ({
                    clearLabel: `Clear ${selection.value} from ${dimension.label} filter`,
                    key: dimension.key,
                    label: dimension.label,
                    selectionKey: selection.key,
                    value: selection.value,
                }));
            }

            const value = dimension.describe(state);

            return value === null
                ? []
                : [
                      {
                          clearLabel: `Clear ${dimension.label} filter`,
                          key: dimension.key,
                          label: dimension.label,
                          value,
                      },
                  ];
        });

    const appliedSummaries = computed(() =>
        describeActiveDimensions(options.applied())
    );
    const appliedActiveKeys = computed(() =>
        appliedActiveDimensions().map((dimension) => dimension.key)
    );
    const summaries = computed(() => describeActiveDimensions(draft()));

    const commitAppliedState = (next: State): void => {
        draft.set(next);
        options.commit(next);
    };

    return {
        activeCount: computed(() => appliedActiveDimensions().length),
        applied: options.applied,
        appliedActiveKeys,
        appliedSummaries,
        draft: draft.asReadonly(),
        isDefault: computed(() =>
            options.definition.equals(
                draft(),
                options.definition.createDefault()
            )
        ),
        isDirty: computed(
            () => !options.definition.equals(draft(), options.applied())
        ),
        summaries,
        apply: () => options.commit(draft()),
        applyNow: (action) => {
            commitAppliedState(
                options.definition.reduce(options.applied(), action)
            );
        },
        begin: () => draft.set(options.applied()),
        cancel: () => draft.set(options.applied()),
        clearDimension: (key) => {
            const dimension = options.definition.dimensions.find(
                (candidate) => candidate.key === key
            );
            const current = draft();

            if (!dimension?.isActive(current)) return;

            draft.set(
                options.definition.reduce(
                    current,
                    dimension.clearAction(current)
                )
            );
        },
        clearSummary: (key, selectionKey) => {
            const dimension = options.definition.dimensions.find(
                (candidate) => candidate.key === key
            );
            const current = draft();

            if (!dimension?.isActive(current)) return;

            const action =
                selectionKey === undefined
                    ? dimension.clearAction(current)
                    : dimension
                          .selections?.(current)
                          .find((selection) => selection.key === selectionKey)
                          ?.clearAction;

            if (action === undefined) return;

            draft.set(options.definition.reduce(current, action));
        },
        dispatch: (action) => {
            draft.update((current) =>
                options.definition.reduce(current, action)
            );
        },
        resetApplied: () => {
            commitAppliedState(options.definition.createDefault());
        },
        resetDraft: () => draft.set(options.definition.createDefault()),
    };
}
